import { ENTER } from '@angular/cdk/keycodes';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NgForm, ValidationErrors, Validators, FormControl, FormGroup } from '@angular/forms';
import { MatLegacyAutocomplete as MatAutocomplete, MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent, MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { forkJoin, Observable, of } from 'rxjs';
import {debounceTime, finalize, map, skipWhile, switchMap, takeUntil, filter} from 'rxjs/operators';
import {cloneDeep, difference, find, isString, remove } from 'lodash-es';

import {
  FindControlAdditionalSearchParametersDto,
  FindControlRequiredSearchParametersDto,
  FindControlResultDto,
  FindControlInitDto,
  GenericSimpleChanges,
  GetDataSearchDto,
  IColumnPk,
  throwInvalidValue,
  PrepopulateType,
  HiddenActionItemType,
  SystemGuid,
} from 'webcommon/shared';
import { ApBaseComponent, setChipAutoCompleteText } from 'webcommon/core-ui';
import { ApFindControlModalService } from './ap-findcontrol-modal.service';
import { FindControlMode } from '../models/index';
import { FindControlIdService } from 'webcommon/legacy-common';
import { AutocompleteGroup } from '../models/AutocompleteGroup';
import { FindControlCustomData } from './FindControlCustomData';
import { ApFindControlService } from './ap-findcontrol.service';

@Directive()
export abstract class ApFindControlBaseComponent<T>
  extends ApBaseComponent
  implements OnInit, OnChanges, Validators, AfterViewInit, AfterViewChecked {

  @Input() baseId: string;
  @Input() findControlId: string;
  @Input() findControlDisplayName: string;
  @Input() requiredSearchParameters: FindControlRequiredSearchParametersDto | null = null;
  @Input() additionalSearchParameters: FindControlAdditionalSearchParametersDto | null = null;
  @Input() singleSelect = false;
  @Input() placeHolder = '';
  @Input() disabled = false;
  @Input() required = false;
  @Input() form?: NgForm;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('apFormGroup') formGroup?: FormGroup | null = null;
  @Input() controlName?: string | null = null;
  @Input() showRequiredOnly = false;
  @Input() autoFocus = false;
  @Input() hiddenActionItems?: HiddenActionItemType[];
  @Input() findControlMaxLength: number | null = null;
  @Input() searchOnly = false;
  @Input() formGroupSubmitted?: boolean | null;
  @Input() messageOverrides?: Record<string, string> = {};
  @Input() customData?: FindControlCustomData; // Using this with the CustomData id type - find control will not hit endpoints
  @Input() initialAutoSearchValue?: string | null = null;

  @Output() dirty = new EventEmitter<boolean>();
  @Output() chipValueChanged = new EventEmitter<boolean>();

  // Really, we should use either selectedPks or selectedPk, but not both.
  @Output() selectedPksChange = new EventEmitter<string[]>();
  protected _selectedPks: string[] = [];
  @Input()
  get selectedPks() {
    return this._selectedPks;
  }
  set selectedPks(val) {
    // Only emit events and update the internal value if the value changes.
    if (this._selectedPks !== val) {
      this._selectedPks = val;

      if (!val) {
        throwInvalidValue('selectedPks', val);
      }
      // set the other property, but not thru the setter, so we don't create a loop
      // there are a few places where this value may still be set to null or undefined,
      // so we need this '?.' conditional check for now, until those places are resolved
      const firstPk = val?.[0] || null;
      this._selectedPk = firstPk;
    }
  }

  @Output() selectedPkChange = new EventEmitter<string | null>();
  protected _selectedPk: string | null = null;
  @Input()
  get selectedPk() {
    return this._selectedPk;
  }
  set selectedPk(val) {
    const normalizedVal = val || null;

    // Only emit events and update the internal value if the value changes.
    if (this._selectedPk !== normalizedVal) {
      this._selectedPk = normalizedVal;

      // set the other property, but not thru the setter, so we don't create a loop
      const newSelectedPks = normalizedVal ? [normalizedVal] : [];
      this._selectedPks = newSelectedPks;
    }
  }

  @Output() chipsChange = new EventEmitter<IColumnPk[]>();

  // useful for singleSelect = true
  @Output() chipChange = new EventEmitter<IColumnPk | null>();
  @Output() modalOpened = new EventEmitter<boolean>();

  @ViewChild(MatAutocompleteTrigger, { static: true }) autocomplete: MatAutocompleteTrigger;

  Id: string;
  visible = true;
  selectable = true;
  removable = true;
  addOnBlur = false;
  // The value can actually be the [value] of an option right after it's selected, not just string | null.
  autoCompleteFormControl = new FormControl<string | null | IColumnPk>(null);
  findControlDto?: FindControlInitDto;
  chips: IColumnPk[] = [];
  // this can hold a reference to the selectedPks that are directly tied to these chips,
  // so that we have something to compare to tell if the selectedPks changed
  protected chipsPksSource: string[] = [];

  loadingConfig = false;  // loading find control config
  loadingItems = false;   // loading selected chip items
  loadingData = false;    // loading search results
  autoCompleteOpen = false;
  updatingmfumru = false;
  focused: boolean;
  initialized = false;
  useCustomData = false;

  readonly separatorKeysCodes: number[] = [ENTER];
  readonly waitTime = 300;
  readonly MFU_MAXCOUNT = 5;
  readonly MRU_MAXCOUNT = 5;

  // TODO: update type here after angular 14 upgrades and other places with form controls to be like this
  autocompleteGroups$: Observable<AutocompleteGroup[]> = this.autoCompleteFormControl.valueChanges.pipe(
    debounceTime(this.waitTime),
    filter((val): val is string | null => {
      return !val || isString(val);
    }),
    skipWhile(() => this.loadingConfig || this.loadingItems), // Don't load info while loading
    map((val) => this.createSearchDto(val)),
    switchMap((searchDto) => this.getSearchResults(searchDto)),
    map((autoCompleteGroups) => this.removeCurrentlySelectedFromData(autoCompleteGroups)),
    map((autocompleteGroups) => this.prependCurrentlySelected(autocompleteGroups)),
  );

  findControlFormControl?: FormControl<T>;

  @ViewChild('chipInput', { static: true }) chipInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto', { static: true }) matAutocomplete: MatAutocomplete;

  @ViewChild('openModalButton', { static: true, read: ElementRef })
  openModalButton: ElementRef<HTMLElement>;

  constructor(
    private apFindControlModalService: ApFindControlModalService,
    private apFindControlService: ApFindControlService,
    protected findControlIdService: FindControlIdService,
    private changeDetector: ChangeDetectorRef,
    private elementInjector: Injector,
    public systemGuid: SystemGuid,
  ) {
    super();
  }

  ngOnInit() {
    this.initialized = true;
    this.setId();
    this.initializeFormControl();
    this.load();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.setAutofocusAndInitialValue();
    });
  }

  load() {
    this.loadingConfig = true;
    // Don't load if no id passed
    if (!this.findControlId) {
      this.loadingConfig = false;
      return;
    }
    if (this.searchOnly) {
      this.hiddenActionItems = [HiddenActionItemType.Delete, HiddenActionItemType.Modify, HiddenActionItemType.New];
    }

    this.useCustomData = (this.findControlId === this.findControlIdService.CustomData && !!this.customData);

    const configObs = this.useCustomData
      ? of(this.customData?.Config as FindControlInitDto)
      : this.apFindControlService.getConfig(this.findControlId);
    configObs.pipe(
      finalize(() => {
        this.loadingConfig = false;
      }),
      this.takeUntilDestroy(),
    ).subscribe(
      (data) => {
        this.onLoadConfigSuccess(data);
        if (this.initialAutoSearchValue) {
          // get the autocomplete list loaded for the initial search value after the config
          this.autoCompleteFormControl.updateValueAndValidity();
        }
      },
      () => this.onError(),
    );
  }

  public setErrorFindControlDisplayName(): string | undefined {
    if (this.findControlDisplayName) {
      return this.findControlDisplayName.includes('Find') ? this.findControlDisplayName.split('Find').pop() : this.findControlDisplayName;
    }
  }

  initializeFormControl() {
    const formGroup = this.form?.form ?? this.formGroup;
    if (!formGroup) { return; }

    const controlName = this.controlName ? this.controlName : this.Id;
    if (formGroup.contains(controlName)) {
      this.findControlFormControl = formGroup.get(controlName) as FormControl<T>;
      this.findControlFormControl.addValidators([this.validate]);
    }
    else {
      this.findControlFormControl = new FormControl<T>(
        this.createFormControlValueOrDefault(),
        this.validate,
      ) as FormControl<T>;
      formGroup.addControl(controlName, this.findControlFormControl);
    }
    this.updateFormControl();
  }

  protected abstract createFormControlValueOrDefault(): T;

  protected abstract matchesCurrentFormControlValue(data: T): boolean;

  protected updateFormControl() {
    if (!this.findControlFormControl) { return; }
    const fcValue = this.createFormControlValueOrDefault();
    if (!this.matchesCurrentFormControlValue(fcValue)) {
      this.findControlFormControl.setValue(fcValue);
    }
    this.findControlFormControl.updateValueAndValidity();
  }

  abstract validate(): ValidationErrors | null;

  public checkIfParentFormGroupHasToBeSubmitted(): boolean {
    if (this.formGroupSubmitted !== null && this.formGroupSubmitted !== undefined) {
      return this.formGroupSubmitted;
    } else {
      return true;
    }
  }

  isRequired(): boolean {
    const required = !this.required ? false : this.required;
    return !this.isShowRequiredOnly() && required;
  }

  isShowRequiredOnly(): boolean {
    return this.showRequiredOnly;
  }

  ngOnChanges(changes: GenericSimpleChanges<ApFindControlBaseComponent<T>>): void {
    if (changes.selectedPks && changes.selectedPk) {
      throw new Error('ApFindControlComponent: Don\'t use selectedPk, and selectedPks together.');
    }

    if (!this.initialized) { return; }

    if (changes.baseId) {
      this.setId();
    }

    if (changes.disabled) {
      this.updateIsDisabled();
    }

    if ((changes.selectedPks || changes.selectedPk)
        && this.findControlDto
    ) { // should be initialized at this point
      const selectedPks = changes.selectedPks?.currentValue ?? this.selectedPks;
      this.updateSelectedChips(selectedPks);
    }

    if (changes.findControlId || changes.customData) {
      this.clearChips();
      this.load();
    }

    if (changes.singleSelect && changes.singleSelect.currentValue) {
      this.truncateChips(1);
    }

    if (changes.required) {
      this.updateFormControl();
    }

  }

  protected updateIsDisabled(): void {
    if (this.findControlFormControl && this.disabled !== this.findControlFormControl.disabled) {
      if (this.findControlFormControl.enabled) {
        this.findControlFormControl.disable();
      }
      else {
        this.findControlFormControl.enable();
      }
    }

    if (this.disabled !== this.autoCompleteFormControl.disabled) {
      if (this.autoCompleteFormControl.enabled) {
        this.autoCompleteFormControl.disable();
      }
      else {
        this.autoCompleteFormControl.enable();
      }
    }
  }

  onApplyDatePickerStyle(): any {
    const obj: any = {};
    const required = !this.required ? false : this.required;
    if (required || this.isShowRequiredOnly()) {
      obj.backgroundColor = '#fafad2';
    }
    return obj;
  }

  protected createSearchDto(searchString: string | null): GetDataSearchDto {
    const searchDto: GetDataSearchDto = {
      RequiredSearchParameters: this.requiredSearchParameters ?? null,
      AdditionalSearchParameters: this.additionalSearchParameters ?? null,
      SearchText: searchString || '',
      ShowInactive: false,
    };
    return searchDto;
  }

  protected prependCurrentlySelected(autocompleteGroup: AutocompleteGroup[]): AutocompleteGroup[] {
    const chipList = this.chips.length ? cloneDeep(this.chips) : [];
    const chipGroup: AutocompleteGroup = {
      AdditionalRows: false,
      GroupDisabled: true,
      ItemList: chipList,
      Name: 'Currently Selected',
      isCollapsable: true,
      isCollapsed: true,
    };
    if (chipList.length > 0) {
      autocompleteGroup.unshift(chipGroup);
    }
    return autocompleteGroup;
  }

  protected removeCurrentlySelectedFromData(autocompleteGroup: AutocompleteGroup[]): AutocompleteGroup[] {
    if (!autocompleteGroup) {
      return [];
    }

    // there are no currently selected items to filter out
    if (!this.chips.length) {
      return autocompleteGroup;
    }

    const currentPks = this.chips.map((chip) => chip.Pk);

    autocompleteGroup.forEach((data) => {
      remove(data.ItemList, (PkItem) => find(currentPks, (cPk) => cPk.toLowerCase() === PkItem.Pk.toLowerCase()));
    });
    return autocompleteGroup;
  }

  protected getSearchResults(searchDto: GetDataSearchDto): Observable<AutocompleteGroup[]> {
    if (this.findControlDto?.FindControlBehavior?.Prepopulate !== PrepopulateType.None
      && this.findControlDto?.FindControlBehavior?.Prepopulate !== PrepopulateType.All) {
      this.loadingData = true;
    }

    let dataArray: Observable<AutocompleteGroup[]>;

    if (this.useCustomData) {
      this.loadingData = false;
      if (!this.customData) {
        return of([]);
      }
      return forkJoin(
        [
          of(this.apFindControlService.getCustomData(searchDto, this.customData)).pipe(
            map((data) => this.formatResults(data, this.formatResultListName(data))),
          ),
        ],
      );
    }

    if (searchDto.SearchText && searchDto.SearchText.length >= (this.findControlDto?.FindControlBehavior?.MinimumSearchCharacters ?? 0)) {
      dataArray = forkJoin(
        [
          this.apFindControlService.getData(this.findControlId, searchDto).pipe(
            map((data) => this.formatResults(data, this.formatResultListName(data))),
          )
        ],
      );
    } else if (this.findControlDto?.FindControlBehavior?.Prepopulate === PrepopulateType.MRU) {
      dataArray = forkJoin(
        [
          this.apFindControlService.getMruList(this.findControlId, this.MRU_MAXCOUNT, searchDto).pipe(
            map((data) => this.formatResults(data, 'Most Recently Used')),
          )
        ],
      );
    } else if (this.findControlDto?.FindControlBehavior?.Prepopulate === PrepopulateType.MFU_Frequency ||
              this.findControlDto?.FindControlBehavior?.Prepopulate === PrepopulateType.MFU_Alpha) {
      dataArray = forkJoin(
        [
          this.apFindControlService.getMfuList(this.findControlId, this.MFU_MAXCOUNT, searchDto).pipe(
            map((data) => this.formatResults(data, 'Most Frequently Used')),
          )
        ],
      );
    }
    else { // prepopulation is all or none
      dataArray = forkJoin(
        [
          this.apFindControlService.getData(this.findControlId, searchDto).pipe(
            map((data) => this.formatResults(data, 'All')),
          )
        ],
      );
    }

    return dataArray.pipe(
      finalize(() => {
        this.loadingData = false;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  protected formatResults(findResults: FindControlResultDto,  name: string): AutocompleteGroup {
    const group: AutocompleteGroup = {
      AdditionalRows: findResults.AdditionalRows,
      GroupDisabled: false,
      ItemList: findResults.Rows,
      Name: name,
      isCollapsable: false,
      isCollapsed: false,
    };

    return group;
  }

  protected formatResultListName(results: FindControlResultDto): string {
    const name = 'Results (' + results.Rows.length + (results.AdditionalRows ? ')' : '+)');
    return name;
  }

  openModal() {
    if (this.disabled || !this.findControlId) { return; }

    this.autoCompleteFormControl.disable();
    if (this.autocomplete?.panelOpen) {
      this.autocomplete.closePanel();
    }
    const chipsCopy = cloneDeep(this.chips);
    this.modalOpened.emit(true);
    this.apFindControlModalService.openModal(
      this.elementInjector,
      this.findControlId,
      chipsCopy,
      this.Id,
      this.findControlDisplayName,
      this.singleSelect ? FindControlMode.SingleSelect : FindControlMode.MultiSelect,
      this.requiredSearchParameters ?? undefined,
      this.additionalSearchParameters ?? undefined,
      this.hiddenActionItems ?? undefined,
      this.customData,
    ).pipe(
      finalize(() => this.autoCompleteFormControl.enable()),
      this.takeUntilDestroy(),
    ).subscribe(
      (result) => {
        if (result.isSuccess) {
          this.chips = result.resultValue;
          this.chipsChanged(this.chips);
        }
      }
    );
  }

  protected onLoadConfigSuccess(data: FindControlInitDto) {
    this.findControlDto = data;
    this.updateSelectedChips(this.selectedPks);
  }

  protected updateSelectedChips(selectedPks: string[] | null) {
    // if selectedPks === chipsPksSource,
    // then the chips and selectedPks are already in sync, so we don't need to update
    if (selectedPks === this.chipsPksSource) {
      return;
    }

    // there are a few places where this value may still be set to null or undefined,
    // so we need this '?.' conditional check for now, until those places are resolved
    if (!selectedPks?.length) {
      // If list is null or empty no need to call endpoint
      this.chips = [];
      this.chipsChanged(this.chips);
    } else  {
      this.loadSelectedItems(selectedPks);
    }
  }

  protected loadSelectedItems(selectedPks: string[]): void {
    this.loadingItems = true;
    const getSelectedItemsObs = this.useCustomData
      ? of(this.apFindControlService.getCustomSelectedItems(selectedPks, this.customData))
      : this.apFindControlService.getSelectedItems(this.findControlId, selectedPks);

    getSelectedItemsObs.pipe(
      finalize(() => {
        this.loadingItems = false;
      }),
      this.takeUntilDestroy(),
    ).subscribe(
      (data) => this.onLoadSelectedItemsSuccess(data),
      () => this.onError(),
    );
  }

  private getAllSelectedItemData(selectedPks: string[]): void {
    this.loadingItems = true;
    // This only seems to be called for a specific FindControlId so the custom data shouldn't ever occur here
    this.apFindControlService.getItem(this.findControlId, selectedPks[0]).pipe(
      finalize(() => {
        this.loadingItems = false;
      }),
      this.takeUntilDestroy(),
    ).subscribe(
      (data) => this.chipsChange.emit([{ Pk: selectedPks[0], ...data }]),
      () => this.onError()
    );
  }

  protected onLoadSelectedItemsSuccess(resultDto: FindControlResultDto) {
    this.chips = resultDto.Rows;
    this.chipsChanged(this.chips);
  }

  protected onError() {
    // TODO implement
  }

  getDisplayName(data: IColumnPk): string {
    return data[this.findControlDto?.FindControlBehavior?.ColumnsConfig.PrimaryDisplayName ?? ''];
  }

  protected getChipDisplayName(data: IColumnPk): string {
    return this.getDisplayName(data);
  }

  getAutocompleteDisplayName(data: IColumnPk): string {
    return this.getDisplayName(data);
  }

  removeChip(object: IColumnPk): void {
    const index = this.chips.map((chip) => chip.Pk).indexOf(object.Pk);

    if (index >= 0) {
      this.chips.splice(index, 1);
      this.chipsChanged(this.chips);
    }
  }

  clearInputField() {
    this.chipInput.nativeElement.value = '';
  }

  // TODO look into click vs enter - different logic occurs
  chipSelectedFromAutoComplete(event: MatAutocompleteSelectedEvent): void {
    const value: IColumnPk | undefined = event.option.value;
    if (!value) { return; }

    if (this.singleSelect) {
      this.chips = [value];
      this.chipsChanged(this.chips);
    } else {
      if (!this.chips.find((chip) => chip.Pk === value.Pk)) {
        this.chips.push(value);
        this.chipsChanged(this.chips);
      }
    }
    this.clearInputField();
    this.autoCompleteFormControl.setValue(null);
  }

  public scrollEvent = (): void => {
    // required for fixing scroll on window, when dropdown is open
    if (this.autocomplete.panelOpen) {
      this.autocomplete.updatePosition();
    }
  };

  onAutoCompleteOpen() {
    this.autoCompleteOpen = true;
    window.addEventListener('scroll', this.scrollEvent, true);
  }

  onAutoCompleteClose() {
    this.autoCompleteOpen = false;
    this.autoCompleteFormControl.setValue('');
    window.removeEventListener('scroll', this.scrollEvent, true);
  }

  onMfuMruSuccess(sentUids: string[], returnedUids: string[]) {
    const comparison = difference(sentUids, returnedUids);
    if (comparison.length !== 0) {
      // something happened where the MFU/MRU was not updated for all sent uids
      // this.onError();
    }
  }

  // Call this after changing this.chips
  protected chipsChanged(chips: IColumnPk[], skipMfu = false): void {
    const chipPks = chips.map((chip) => chip.Pk);
    // there are a few places where this value may still be set to null or undefined,
    // so we need this default value for now, until those places are resolved
    const selectedPks = this.selectedPks || [];
    const diffList = difference(chipPks, selectedPks);

    if (!this.useCustomData && !skipMfu && diffList.length > 0) {
      this.updatingmfumru = true;
      this.apFindControlService.updateMfuMruList(this.findControlId, diffList).pipe(
        finalize(() => {
          this.updatingmfumru = false;
        }),
        takeUntil(this.onDestroy$),
      ).subscribe(
        (data) => this.onMfuMruSuccess(diffList, data),
        () => this.onError(), // TODO We don't really care if this fails, maybe remove this
      );
    }

    // if the selectedPks are out of sync with chips, then update it
    if (diffList.length > 0 || chipPks.length !== selectedPks.length) {
      this.chipsPksSource = chipPks;
      this.selectedPks = chipPks;
      this.selectedPksChange.emit(chipPks);
      this.selectedPkChange.emit(this.selectedPk);
      // TODO only fire when changed
      this.dirty.emit(true);
      this.chipValueChanged.emit(true);
      this.findControlFormControl?.markAsDirty();
    } else {
      this.chipValueChanged.emit(false);
    }
    this.updateFormControl();

    switch (true) {
      case this.baseId === 'fc-pharmacy-item' && this.findControlId && this.findControlId === 'ViewMasterPharmacy' && chipPks.length > 0:
      case (
        this.baseId === 'fc-insurance-plan-item' &&
        this.findControlId === FindControlIdService.ViewInsuranceCarrier &&
        chipPks.length > 0
      ):
        this.getAllSelectedItemData(chipPks);
        break;
      default:
        this.chipsChange.emit(chips);
    }
    this.chipChange.emit(chips[0] || null);
  }

  protected truncateChips(listSize: number) {
    if (!this.chips) { return; }
    this.chips.splice(listSize);
    this.chipsChanged(this.chips);
  }

  protected clearChips(): void {
    this.truncateChips(0);
  }

  ngAfterViewChecked() {
    this.changeDetector.detectChanges();
  }

  protected setId() {
    this.Id = `${this.baseId}-findcontrol`;
  }

  toggleCollapsed(event: Event, group: AutocompleteGroup) {
    event.stopPropagation();
    event.preventDefault();
    group.isCollapsed = !group.isCollapsed;
  }

  inputFocused() {
    if (!this.loadingConfig && !this.loadingItems) {
      // I think this is currently necessary, because if the chips, or requiredSearchParameters, or additionalSearchParameters
      // have changed since the last time this was focused, then it can redo the autocomplete group list
      // to match these values.
      // Also, this will get the list loaded the first time.
      this.autoCompleteFormControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    }

    this.focused = true;
  }

  inputBlurred(): void {
    this.focused = false;
  }

  // The current purpose of this mainly is so that when you click on a chip
  // or something in the mat-form-field that is not the actual input box, it will still focus the input box,
  // and allow you to type.
  findControlClicked(event?: Event) {
    // don't focus the input if click came from modal button
    if (event?.target instanceof Node && this.openModalButton.nativeElement.contains(event.target)) {
      return;
    }

    this.tryFocusInput();
  }

  shouldExpand(): boolean {
    const hasChips = this.selectedPks?.length > 0;
    const isFocused = this.focused || this.autoCompleteOpen;

    const shouldExpand = (isFocused) || (!isFocused && !hasChips);

    return shouldExpand;
  }

  setAutofocusAndInitialValue() {
    if (this.initialAutoSearchValue) {
      setChipAutoCompleteText(this.initialAutoSearchValue, this.chipInput.nativeElement, this.autoCompleteFormControl);
    }

    if (this.autoFocus) {
      this.tryFocusInput();
    }
  }

  tryFocusInput() {
    if (this.disabled) {
      return;
    }

    if (!this.chipInput) {
      return;
    }
    // Focus the input box and open the autocomplete
    this.chipInput.nativeElement.focus();
  }
}
